<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const accountId = computed(() => store.getters.getCurrentAccountId);

// Feature flag + role check (mirrors backend policy)
const account = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const settings = computed(() => account.value.settings || {});

const hasFeatureAccess = computed(() => {
  const enabled =
    settings.value.gestion_plus_enabled === true ||
    settings.value.gestion_plus_enabled === 'true';
  if (!enabled) return false;

  const role = store.getters.getCurrentRole;
  if (role === 'administrator') return true;

  const allowedRoles = Array.isArray(settings.value.gestion_plus_roles)
    ? settings.value.gestion_plus_roles.map(r => String(r))
    : [];
  if (!allowedRoles.length) return false;

  const customRoleId = store.getters.getCurrentCustomRoleId;
  const userRoles = [];
  if (role) userRoles.push(String(role));
  if (customRoleId) userRoles.push(String(customRoleId));
  return userRoles.some(r => allowedRoles.includes(r));
});

const phone = computed(
  () => props.conversation?.meta?.sender?.phone_number || ''
);

const isVisible = computed(() => hasFeatureAccess.value && phone.value);

const isBusy = computed(
  () =>
    store.getters['zohoCrmIntegration/getUIFlags'].isSearching ||
    store.getters['zohoCrmIntegration/getUIFlags'].isMarking
);

// Reflects whether the lead in Zoho already has Pasar_a_estado_Gestion_Plus.
// Populated by a silent lookup when the conversation loads and by the mark
// action when the agent flips it from the modal. Keeps state per-conversation
// (resets via the watcher below).
const isMarked = ref(false);
const isCheckingState = ref(false);

// Ensures the Zoho connection status is in the store before deciding whether
// to query the CRM. The fetch is idempotent and runs only once per page load
// when the feature is enabled, so it's safe to call from every button mount.
let statusFetchPromise = null;
const ensureStatusLoaded = () => {
  if (statusFetchPromise) return statusFetchPromise;
  statusFetchPromise = store.dispatch('zohoCrmIntegration/fetchStatus');
  return statusFetchPromise;
};

const refreshMarkedState = async () => {
  if (!isVisible.value) {
    // eslint-disable-next-line no-console
    console.log('[gestion-plus] skipped state lookup, not visible yet');
    return;
  }
  await ensureStatusLoaded();
  if (!store.getters['zohoCrmIntegration/isConnected']) {
    // eslint-disable-next-line no-console
    console.log('[gestion-plus] skipped state lookup, not connected');
    return;
  }
  isCheckingState.value = true;
  try {
    const results = await store.dispatch(
      'zohoCrmIntegration/searchLeads',
      phone.value
    );
    // Zoho's Pasar_a_estado_Gestion_Plus is a one-shot trigger (flipped back
    // to false after the workflow runs). The persistent indicator is the
    // Lead_Status field, which gets set to "En Gestión Plus" by the workflow.
    isMarked.value = (results || []).some(
      l => l.Lead_Status === 'En Gestión Plus'
    );
    // eslint-disable-next-line no-console
    console.log(
      '[gestion-plus] state for',
      phone.value,
      '→ isMarked=',
      isMarked.value
    );
  } catch (e) {
    // Silent on the initial lookup — surface the error only when the agent
    // explicitly clicks the button.
    // eslint-disable-next-line no-console
    console.warn('[gestion-plus] state lookup failed:', e?.message);
  } finally {
    isCheckingState.value = false;
  }
};

// Re-run the lookup whenever the conversation OR the contact phone changes.
// The conversation arrives first with id but without meta.sender, so watching
// the id alone misses the phone, and a page refresh would never trigger the
// lookup. Watching `phone` (or any meaningful change) covers both cases.
watch(
  () => [props.conversation?.id, phone.value],
  ([, newPhone], oldArr) => {
    const oldPhone = oldArr ? oldArr[1] : null;
    if (newPhone === oldPhone) return;
    isMarked.value = false;
    refreshMarkedState();
  },
  { immediate: true }
);

// Hard refresh: also retry shortly after mount to recover from the case where
// the conversation hadn't loaded its meta.sender yet on first render.
onMounted(() => {
  refreshMarkedState();
});

// Stages: idle → confirm → searching → choose → marking
const stage = ref('idle');
const leads = ref([]);
const selectedLeadId = ref(null);

const openConfirm = () => {
  if (isBusy.value) return;
  stage.value = 'confirm';
};

const cancel = () => {
  stage.value = 'idle';
  leads.value = [];
  selectedLeadId.value = null;
};

const performMark = async leadId => {
  try {
    await store.dispatch('zohoCrmIntegration/markGestionPlus', leadId);
    isMarked.value = true;
    useAlert(t('GESTION_PLUS.SUCCESS'));
    // eslint-disable-next-line no-console
    console.log('[gestion-plus] marked lead', leadId);
    cancel();
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[gestion-plus] mark failed:', msg);
    useAlert(t('GESTION_PLUS.MARK_ERROR', { error: msg }));
  }
};

const confirmAndSearch = async () => {
  // eslint-disable-next-line no-console
  console.log('[gestion-plus] searching for phone', phone.value);
  stage.value = 'searching';
  try {
    const results = await store.dispatch(
      'zohoCrmIntegration/searchLeads',
      phone.value
    );
    leads.value = results;
    if (results.length === 0) {
      useAlert(t('GESTION_PLUS.NOT_FOUND', { phone: phone.value }));
      cancel();
      return;
    }
    if (results.length === 1) {
      stage.value = 'marking';
      await performMark(results[0].id);
      return;
    }
    // Multiple matches → let the agent choose
    selectedLeadId.value = results[0].id;
    stage.value = 'choose';
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[gestion-plus] search failed:', msg);
    useAlert(t('GESTION_PLUS.SEARCH_ERROR', { error: msg }));
    cancel();
  }
};

const markSelected = async () => {
  if (!selectedLeadId.value) return;
  stage.value = 'marking';
  await performMark(selectedLeadId.value);
};

const leadLabel = lead => {
  const name = [lead.First_Name, lead.Last_Name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || lead.Email || lead.Mobile || lead.id;
};
</script>

<template>
  <div v-if="isVisible" class="relative">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      :class="
        isMarked
          ? 'bg-n-teal-3 border-n-teal-7 text-n-teal-11 hover:bg-n-teal-4'
          : 'border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1'
      "
      :disabled="isBusy"
      :title="
        isMarked ? t('GESTION_PLUS.TOOLTIP_MARKED') : t('GESTION_PLUS.TOOLTIP')
      "
      @click="openConfirm"
    >
      <span
        class="size-3.5"
        :class="
          isBusy || isCheckingState
            ? 'i-lucide-loader-2 animate-spin'
            : isMarked
              ? 'i-lucide-badge-check'
              : 'i-lucide-shield-check'
        "
      />
      <span class="hidden md:inline">
        {{
          isMarked ? t('GESTION_PLUS.BUTTON_MARKED') : t('GESTION_PLUS.BUTTON')
        }}
      </span>
    </button>

    <!-- Confirm modal -->
    <Teleport to="body">
      <div
        v-if="stage !== 'idle'"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-3"
        @click.self="cancel"
      >
        <div
          class="bg-n-background rounded-xl shadow-xl border border-n-weak max-w-md w-full mx-4 p-5"
        >
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('GESTION_PLUS.MODAL_TITLE') }}
          </h3>

          <!-- Confirm step -->
          <div v-if="stage === 'confirm'" class="mt-3 text-sm text-n-slate-11">
            <p>
              {{ t('GESTION_PLUS.CONFIRM_PROMPT', { phone }) }}
            </p>
            <div class="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                class="rounded-lg bg-transparent px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                @click="cancel"
              >
                {{ t('GESTION_PLUS.CANCEL') }}
              </button>
              <button
                type="button"
                class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                @click="confirmAndSearch"
              >
                {{ t('GESTION_PLUS.CONFIRM') }}
              </button>
            </div>
          </div>

          <!-- Searching step -->
          <div
            v-else-if="stage === 'searching' || stage === 'marking'"
            class="mt-5 flex items-center justify-center gap-2 py-6 text-n-slate-11"
          >
            <span class="i-lucide-loader-2 size-5 animate-spin" />
            <span>
              {{
                stage === 'searching'
                  ? t('GESTION_PLUS.SEARCHING')
                  : t('GESTION_PLUS.MARKING')
              }}
            </span>
          </div>

          <!-- Choose step (multiple leads) -->
          <div v-else-if="stage === 'choose'" class="mt-3">
            <p class="text-sm text-n-slate-11">
              {{ t('GESTION_PLUS.CHOOSE_PROMPT', { count: leads.length }) }}
            </p>
            <div
              class="mt-3 max-h-72 overflow-y-auto rounded-lg border border-n-weak divide-y divide-n-weak"
            >
              <button
                v-for="lead in leads"
                :key="lead.id"
                type="button"
                class="w-full flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 text-left transition-colors"
                :class="{ 'bg-n-alpha-1': selectedLeadId === lead.id }"
                @click="selectedLeadId = lead.id"
              >
                <span
                  class="mt-0.5 flex-shrink-0 size-4 rounded-full border flex items-center justify-center transition-colors"
                  :class="
                    selectedLeadId === lead.id
                      ? 'border-n-brand'
                      : 'border-n-slate-6'
                  "
                >
                  <span
                    v-if="selectedLeadId === lead.id"
                    class="size-2 rounded-full bg-n-brand"
                  />
                </span>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ leadLabel(lead) }}
                  </p>
                  <p class="text-xs text-n-slate-10 truncate">
                    {{ lead.Mobile || lead.Phone || '—' }} ·
                    {{ lead.Email || '—' }}
                  </p>
                  <p
                    v-if="lead.Lead_Status === 'En Gestión Plus'"
                    class="mt-0.5 text-[11px] text-n-amber-11"
                  >
                    {{ t('GESTION_PLUS.ALREADY_MARKED') }}
                  </p>
                </div>
              </button>
            </div>
            <div class="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                class="rounded-lg bg-transparent px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                @click="cancel"
              >
                {{ t('GESTION_PLUS.CANCEL') }}
              </button>
              <button
                type="button"
                class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                :disabled="!selectedLeadId"
                @click="markSelected"
              >
                {{ t('GESTION_PLUS.CONFIRM') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
